import { useState } from 'react';
import api from '../lib/api';
import { Button } from './ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from './ui/dialog';
import { toast } from 'sonner';
import { Upload, FileSpreadsheet, Download, CheckCircle2, AlertTriangle } from 'lucide-react';

/**
 * CSV bulk import dialog for hiring volunteers/interns/full-time employees.
 * Renders the entry button + dialog. Calls onDone() after a successful import
 * so the parent can refresh its list.
 */
export default function BulkImportDialog({ onDone }) {
  const [open, setOpen] = useState(false);
  const [file, setFile] = useState(null);
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);

  const reset = () => { setFile(null); setResult(null); setBusy(false); };

  const downloadTemplate = async () => {
    try {
      const { data } = await api.get('/employees/bulk-import/template', { responseType: 'blob' });
      const url = URL.createObjectURL(data);
      const a = document.createElement('a');
      a.href = url;
      a.download = 'lanvaus-bulk-import-template.csv';
      document.body.appendChild(a); a.click();
      setTimeout(() => { URL.revokeObjectURL(url); a.remove(); }, 1000);
    } catch (e) {
      toast.error('Could not download template');
    }
  };

  const runImport = async () => {
    if (!file) { toast.error('Please choose a CSV file first'); return; }
    setBusy(true);
    try {
      const form = new FormData();
      form.append('file', file);
      const { data } = await api.post('/employees/bulk-import', form, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      setResult(data);
      if (data.succeeded > 0) {
        toast.success(`Imported ${data.succeeded} of ${data.processed} rows`);
        onDone && onDone();
      } else {
        toast.error(`No rows imported — ${data.failed} errors`);
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Bulk import failed');
    } finally {
      setBusy(false);
    }
  };

  const downloadErrors = () => {
    if (!result?.errors?.length) return;
    const headers = ['row', 'name', 'personal_email', 'error'];
    const rows = [headers.join(',')].concat(
      result.errors.map(e => headers.map(h => `"${String(e[h] ?? '').replace(/"/g, '""')}"`).join(','))
    );
    const blob = new Blob([rows.join('\n')], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `bulk-import-errors-${new Date().toISOString().slice(0, 19).replace(/[:T]/g, '-')}.csv`;
    document.body.appendChild(a); a.click();
    setTimeout(() => { URL.revokeObjectURL(url); a.remove(); }, 1000);
  };

  return (
    <Dialog open={open} onOpenChange={(v) => { setOpen(v); if (!v) reset(); }}>
      <DialogTrigger asChild>
        <Button data-testid="bulk-import-button" variant="outline"
                className="rounded-full px-5 h-11 border-slate-300">
          <FileSpreadsheet className="w-4 h-4 mr-2" strokeWidth={1.8} /> Bulk import (CSV)
        </Button>
      </DialogTrigger>
      <DialogContent data-testid="bulk-import-dialog" className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="font-heading text-2xl">Bulk import employees</DialogTitle>
          <p className="text-sm text-slate-500 mt-1">
            Import volunteers, interns or full-time employees from a CSV. Valid rows are processed; invalid rows are returned with diagnostics you can download.
          </p>
        </DialogHeader>

        {!result && (
          <div className="space-y-5 mt-2">
            <div className="rounded-xl border border-slate-200 bg-slate-50 p-4 text-sm text-slate-700">
              <div className="font-semibold mb-1.5 text-slate-900">CSV format</div>
              <p>Required columns: <code className="bg-white px-1.5 py-0.5 rounded border">name</code>,
                {' '}<code className="bg-white px-1.5 py-0.5 rounded border">personal_email</code>.</p>
              <p className="mt-1">Optional: <code className="text-xs">mode</code> (offer | existing),
                {' '}<code className="text-xs">role</code>,
                {' '}<code className="text-xs">title</code>,
                {' '}<code className="text-xs">department</code>,
                {' '}<code className="text-xs">reporting_manager_email</code>,
                {' '}<code className="text-xs">employment_type</code> (full_time | contract | internship | freelancer | volunteer),
                {' '}<code className="text-xs">salary_period</code>,
                {' '}<code className="text-xs">pay_amount</code>,
                {' '}<code className="text-xs">joined_at</code>,
                {' '}<code className="text-xs">branch_name</code>,
                {' '}<code className="text-xs">work_email</code>,
                {' '}<code className="text-xs">password</code>.</p>
              <Button data-testid="download-template-button" variant="outline" size="sm" onClick={downloadTemplate}
                      className="mt-3 rounded-full h-8 text-xs">
                <Download className="w-3.5 h-3.5 mr-1.5" strokeWidth={1.8} /> Download template
              </Button>
            </div>

            <div>
              <label htmlFor="bulk-csv" className="block text-xs font-semibold uppercase tracking-[0.18em] text-slate-600 mb-2">
                Choose CSV file
              </label>
              <input id="bulk-csv" data-testid="bulk-csv-input" type="file" accept=".csv"
                     onChange={(e) => setFile(e.target.files?.[0] || null)}
                     className="block w-full text-sm text-slate-700 file:mr-3 file:py-2 file:px-4 file:rounded-full
                                file:border-0 file:text-sm file:font-semibold file:bg-[#3D4A8E] file:text-white
                                hover:file:bg-[#2F3A6E] cursor-pointer" />
              {file && (
                <p className="mt-2 text-xs text-slate-500">
                  Selected: <span className="font-medium text-slate-700">{file.name}</span>{' '}
                  ({(file.size / 1024).toFixed(1)} KB)
                </p>
              )}
            </div>
          </div>
        )}

        {result && (
          <div className="space-y-4 mt-2" data-testid="bulk-import-result">
            <div className="grid grid-cols-3 gap-3">
              <div className="rounded-xl border border-slate-200 p-4">
                <div className="text-xs font-bold uppercase tracking-[0.18em] text-slate-500">Processed</div>
                <div className="font-heading text-3xl font-bold mt-1 text-slate-900" data-testid="bulk-processed">{result.processed}</div>
              </div>
              <div className="rounded-xl border border-emerald-200 bg-emerald-50 p-4">
                <div className="text-xs font-bold uppercase tracking-[0.18em] text-emerald-700 flex items-center gap-1.5">
                  <CheckCircle2 className="w-3.5 h-3.5" /> Imported
                </div>
                <div className="font-heading text-3xl font-bold mt-1 text-emerald-700" data-testid="bulk-succeeded">{result.succeeded}</div>
              </div>
              <div className="rounded-xl border border-rose-200 bg-rose-50 p-4">
                <div className="text-xs font-bold uppercase tracking-[0.18em] text-rose-700 flex items-center gap-1.5">
                  <AlertTriangle className="w-3.5 h-3.5" /> Errors
                </div>
                <div className="font-heading text-3xl font-bold mt-1 text-rose-700" data-testid="bulk-failed">{result.failed}</div>
              </div>
            </div>

            {result.errors?.length > 0 && (
              <div className="rounded-xl border border-rose-200 bg-rose-50/50 p-4">
                <div className="flex items-center justify-between mb-2">
                  <div className="text-sm font-semibold text-rose-900">Failed rows</div>
                  <Button data-testid="download-errors-button" variant="outline" size="sm" onClick={downloadErrors}
                          className="rounded-full h-8 text-xs">
                    <Download className="w-3.5 h-3.5 mr-1.5" strokeWidth={1.8} /> Download errors
                  </Button>
                </div>
                <div className="max-h-48 overflow-y-auto text-xs space-y-1.5">
                  {result.errors.slice(0, 20).map((err, i) => (
                    <div key={i} className="flex items-start gap-2 bg-white border border-rose-200 rounded-lg px-3 py-2">
                      <span className="font-mono text-rose-600 font-semibold whitespace-nowrap">Row {err.row}</span>
                      <span className="text-slate-700 truncate">{err.name || err.personal_email || '—'}</span>
                      <span className="text-rose-700 ml-auto text-right">{err.error}</span>
                    </div>
                  ))}
                  {result.errors.length > 20 && (
                    <p className="text-xs text-slate-500 pt-1 italic">…and {result.errors.length - 20} more. Download the CSV to see all.</p>
                  )}
                </div>
              </div>
            )}
          </div>
        )}

        <DialogFooter className="mt-4">
          {!result ? (
            <>
              <Button variant="outline" onClick={() => setOpen(false)} className="rounded-full" data-testid="bulk-cancel">
                Cancel
              </Button>
              <Button onClick={runImport} disabled={!file || busy}
                      className="rounded-full bg-[#3D4A8E] hover:bg-[#2F3A6E]" data-testid="run-bulk-import">
                <Upload className="w-4 h-4 mr-2" strokeWidth={1.8} />
                {busy ? 'Importing…' : 'Run import'}
              </Button>
            </>
          ) : (
            <Button onClick={() => setOpen(false)}
                    className="rounded-full bg-[#3D4A8E] hover:bg-[#2F3A6E]" data-testid="bulk-done">
              Done
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
